<script lang="ts" module>
  /* ═══ ONE PLACE THAT KNOWS WHAT A THING LOOKS LIKE ═══
   *
   * Until now the icons were typographic: `◈` for a trip, `⌸` for the tools,
   * `∿` for the current field, `⬍` for a tide station. They came from Chivo
   * Mono and they were a stopgap that looked like one — a chart plotter whose
   * navigation is made of dingbats reads as unfinished however good the
   * typography around it is.
   *
   * Lucide, per-icon, bundled. Not a font and not a sprite sheet: each icon is
   * a Svelte component, so an app pays for the ones it names and nothing else,
   * and — the reason it matters here — there is no network request. This chart
   * has to draw with no internet at all, which is why the three text faces are
   * self-hosted; an icon set fetched from a CDN would be the one thing on
   * screen that vanished offshore.
   *
   * ═══ SEMANTIC NAMES, NOT LUCIDE NAMES ═══
   *
   * A panel asks for `trip`, not for `route`; the layer bar asks for
   * `current-field`, not for `audio-waveform`. Two reasons, and the second is
   * the real one.
   *
   * Callers should not have to know an icon library exists — swapping Lucide
   * for something else should be this file and nothing else. And more
   * importantly, the mapping is an EDITORIAL judgement: `sunrise` for "when to
   * leave" is a decision about what a departure means to a sailor, and it
   * belongs written down in one place rather than repeated as a magic string
   * across nine registrations.
   */
  import Anchor from '@lucide/svelte/icons/anchor';
  import ArrowLeftRight from '@lucide/svelte/icons/arrow-left-right';
  import AudioWaveform from '@lucide/svelte/icons/audio-waveform';
  import Bookmark from '@lucide/svelte/icons/bookmark';
  import ChevronUp from '@lucide/svelte/icons/chevron-up';
  import Clock from '@lucide/svelte/icons/clock';
  import Contrast from '@lucide/svelte/icons/contrast';
  import Crosshair from '@lucide/svelte/icons/crosshair';
  import Database from '@lucide/svelte/icons/database';
  import Ellipsis from '@lucide/svelte/icons/ellipsis';
  import Fish from '@lucide/svelte/icons/fish';
  import FishOff from '@lucide/svelte/icons/fish-off';
  import Layers from '@lucide/svelte/icons/layers';
  import Map from '@lucide/svelte/icons/map';
  import MapPinned from '@lucide/svelte/icons/map-pinned';
  import MessageSquare from '@lucide/svelte/icons/message-square';
  import Minus from '@lucide/svelte/icons/minus';
  import Moon from '@lucide/svelte/icons/moon';
  import ChevronsUpDown from '@lucide/svelte/icons/chevrons-up-down';
  import MoveVertical from '@lucide/svelte/icons/move-vertical';
  import Plus from '@lucide/svelte/icons/plus';
  import Route from '@lucide/svelte/icons/route';
  import Ruler from '@lucide/svelte/icons/ruler';
  import Search from '@lucide/svelte/icons/search';
  import Sun from '@lucide/svelte/icons/sun';
  import SunMoon from '@lucide/svelte/icons/sun-moon';
  import Sunrise from '@lucide/svelte/icons/sunrise';
  import Thermometer from '@lucide/svelte/icons/thermometer';
  import Wind from '@lucide/svelte/icons/wind';
  import X from '@lucide/svelte/icons/x';

  /** Every icon this design system has a name for. Adding one means adding it
   *  here, which is the point: an icon nobody chose is an icon nobody can
   *  explain. */
  export const ICONS = {
    /* ── panels ─────────────────────────────────────────────────────────── */
    /** A path with waypoints on it, which is exactly what a trip is. */
    trip: Route,
    /** "When to leave". Not a clock — the time cursor is a clock and the two
     *  must not read as the same idea. A departure on this coast is usually
     *  timed to a slack, and often to first light. */
    depart: Sunrise,
    search: Search,
    /** Saved, not merely listed. */
    trips: Bookmark,
    layers: Layers,
    /** The time cursor. Rarely drawn — the entry shows its READING instead —
     *  but it is the fallback before the clock has a value. */
    time: Clock,
    /** The tools are route, measure and 3D; the ruler is the one you reach for
     *  and the one people look for. */
    tools: Ruler,
    /** What is under the finger. */
    pick: Crosshair,
    cache: Database,
    more: Ellipsis,

    /* ── chart layers ───────────────────────────────────────────────────── */
    chart: Map,
    /** The second chart authority. `map-plus` was tried and read as "add a
     *  map"; a pinned map reads as another chart of the same place, which is
     *  what NOAA's is. */
    'chart-alt': MapPinned,
    /** Depth shading: a disc half in shadow, which is what the old `◐` was
     *  reaching for. */
    depth: Contrast,
    seamarks: Anchor,
    /** A tide station rises and falls. */
    tides: MoveVertical,
    /** The second authority's stations. The same idea in a different mark —
     *  two identical glyphs side by side is a bar you have to read the tooltips
     *  of. `move-diagonal` was tried and reads as "resize", which is a control,
     *  not a measurement; a double chevron still says up and down. */
    'tides-alt': ChevronsUpDown,
    /** A stream sets one way and then the other. */
    currents: ArrowLeftRight,
    /** The modelled field, as a wave rather than an arrow: it is a surface, not
     *  a direction at a point. */
    'current-field': AudioWaveform,
    wind: Wind,
    'sea-surface': Thermometer,
    fishing: Fish,
    /** A closure. The crossed-out fish is the one icon here that states a
     *  PROHIBITION, and rockfish conservation areas are exactly that. */
    'no-fishing': FishOff,
    /** Somebody's opinion of the water, which is what an anchorage note is. */
    notes: MessageSquare,

    /* ── chrome ─────────────────────────────────────────────────────────── */
    'zoom-in': Plus,
    'zoom-out': Minus,
    close: X,
    expand: ChevronUp,
    day: Sun,
    night: Moon,
    /** Auto: flips at 19:00 and 07:00 local. */
    auto: SunMoon
  } as const;

  export type IconName = keyof typeof ICONS;
</script>

<script lang="ts">
  let {
    name,
    /** Matches the type scale rather than Lucide's 24 px default: an icon
     *  beside `--fs-body` text at 24 px is a picture with a caption. */
    size = 18,
    /** Lucide ships at 2. At 16–18 px that is heavy next to Space Grotesk and
     *  reads as a different weight of thing; 1.75 sits with the text. */
    strokeWidth = 1.75
  }: { name: IconName; size?: number; strokeWidth?: number } = $props();

  const Cmp = $derived(ICONS[name]);
</script>

<!-- `aria-hidden` always. Every place these are used already carries a real
     label — a rail button's `.lb`, a `title`, an `aria-label` — and an icon
     that also announces itself makes a screen reader say everything twice. -->
<Cmp {size} stroke-width={strokeWidth} aria-hidden="true" focusable="false" />
